import React, { useEffect, useState } from 'react';
import { fetchCaseHandoff, type CaseHandoffDetail, type HandoffSeat } from '../../services/handoffApi';

const SHORT = ['SETUP', 'FOUND', 'METHOD', 'CALC', 'ATTEST', 'DELIVER'];
const STAGE_TITLE: Record<string, string> = {
  setup: 'Setup',
  foundation: 'Foundation',
  method: 'Method',
  calculate: 'Calculate',
  attest: 'Attest',
  deliver: 'Deliver',
};
const SEAT_TITLE: Record<HandoffSeat, string> = {
  data_specialist: 'Data Specialist',
  accountant: 'Accountant',
  attorney: 'Attorney',
};

function fmtTs(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Rail block — owns its one fetch for the selected case. */
export default function HandoffBlock({ caseId }: { caseId: string }) {
  const [data, setData] = useState<CaseHandoffDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setFailed(false);
    fetchCaseHandoff(caseId, controller.signal)
      .then((d) => {
        if (!controller.signal.aborted) setData(d || null);
      })
      .catch((e: any) => {
        if (controller.signal.aborted || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return;
        setFailed(true);
      });
    return () => controller.abort();
  }, [caseId]);

  const label = (
    <div className="text-xs uppercase tracking-wide text-text-dim font-medium">Handoff</div>
  );

  if (failed) return null;
  if (!data) {
    return (
      <div className="space-y-2" data-testid="handoff-block">
        {label}
        <div className="h-16 border border-border-faint rounded-[2px]" aria-busy="true" />
      </div>
    );
  }

  const idx = Math.min(6, Math.max(1, data.stage_index || 1));
  const nextText = data.next_action || (data.next_seat ? SEAT_TITLE[data.next_seat] : null);
  const soTs = fmtTs(data.signed_off_at);
  const atTs = fmtTs(data.attested_at);

  return (
    <div className="space-y-2" data-testid="handoff-block">
      {label}
      <div className="flex items-start justify-between" aria-hidden="true">
        {SHORT.map((s, i) => (
          <div key={s} className="flex flex-col items-start gap-1" style={{ width: `${100 / 6}%` }}>
            <span
              style={{
                width: '100%',
                maxWidth: 28,
                height: 4,
                display: 'block',
                boxSizing: 'border-box',
                ...(i + 1 === idx
                  ? { backgroundColor: 'var(--color-brand)' }
                  : i + 1 < idx
                    ? { backgroundColor: 'var(--color-text-dim)', opacity: 0.4 }
                    : { border: '1px solid var(--color-text-dim)', opacity: 0.2 }),
              }}
            />
            <span
              className="uppercase"
              style={{
                fontSize: 9,
                letterSpacing: '0.04em',
                color: i + 1 === idx ? 'var(--color-text-primary)' : 'var(--color-text-dim)',
                fontWeight: i + 1 === idx ? 600 : 400,
              }}
            >
              {s}
            </span>
          </div>
        ))}
      </div>
      <div className="text-xs space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
        <div style={{ color: 'var(--color-text-primary)' }}>
          Stage {idx} of 6 — {STAGE_TITLE[data.current_stage] || data.current_stage}
        </div>
        {nextText && (
          <div className="uppercase" style={{ letterSpacing: '0.06em', fontSize: 11 }}>
            NEXT · {nextText}
          </div>
        )}
        {data.overdue ? (
          <div style={{ color: 'var(--color-blocker)' }}>Signed off · Overdue</div>
        ) : (
          data.signed_off_by && (
            <div>
              <span style={{ color: 'var(--color-verified)' }}>Signed off</span> · {data.signed_off_by}
              {soTs && <span className="font-mono text-[11px]"> · {soTs}</span>}
            </div>
          )
        )}
        {data.attested_by ? (
          <div>
            <span style={{ color: 'var(--color-verified)' }}>Attested</span> · {data.attested_by}
            {atTs && <span className="font-mono text-[11px]"> · {atTs}</span>}
          </div>
        ) : (
          <div style={{ opacity: 0.35 }}>– Attested · Pending</div>
        )}
      </div>
    </div>
  );
}
